import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { companiesAPI, employeesAPI } from '../../../services/api';

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white";
const labelCls = "block text-xs font-medium text-gray-700 mb-1";

const EditContactModal = ({ contact, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    companyName: null, company_id: '', jobTitle: '', department: '',
    leadStatus: '', leadSource: '', linkedInUrl: '', assignedTo: null,
    doNotCall: false, doNotEmail: false,
  });
  const [errors, setErrors] = useState({});

  // Fetch companies and employees on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const response = await companiesAPI.getAllCompanies();
        const formatted = (response.data || []).map(c => ({
          value: c.companyName,
          label: c.companyName,
          id: c.id
        }));
        setCompanies(formatted);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      } finally {
        setCompaniesLoading(false);
      }
    };
    
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const response = await employeesAPI.getAllEmployees();
        const empList = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const formatted = empList.map(emp => ({
          value: emp.id,
          label: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.id,
          id: emp.id
        }));
        setEmployees(formatted);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      } finally {
        setEmployeesLoading(false);
      }
    };
    
    fetchCompanies();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!contact) return;
    const selectedOwner = contact.assignedTo ? employees.find(e => e.value === contact.assignedTo) : null;
    setForm({
      firstName:   contact.firstName   || '',
      lastName:    contact.lastName    || '',
      phone:       contact.phone       || '',
      email:       contact.email       || '',
      companyName: contact.companyName ? { value: contact.companyName, label: contact.companyName, id: contact.company_id } : null,
      company_id:  contact.company_id  || '',
      jobTitle:    contact.jobTitle    || '',
      department:  contact.department  || '',
      leadStatus:  contact.leadStatus  || '',
      leadSource:  contact.leadSource  || '',
      linkedInUrl: contact.linkedInUrl || '',
      assignedTo:  selectedOwner,
      doNotCall:   contact.doNotCall   || false,
      doNotEmail:  contact.doNotEmail  || false,
    });
  }, [contact, employees]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setForm(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors(prev => { const n = {...prev}; delete n[name]; return n; });
  };

  const handleSelectChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!form.firstName?.trim()) errs.firstName = 'First name is required';
    
    // Email validation
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (form.phone) {
      const phoneDigits = String(form.phone).replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errs.phone = 'Please enter a valid 10-digit phone number';
      }
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error('Please fix the errors before saving');
      return;
    }
    setLoading(true);
    try { 
      const payload = {
        ...form,
        company_id: form.companyName?.id || '',
        companyName: form.companyName?.value || '',
        assignedTo: form.assignedTo?.value || null,
      };
      await onSave(contact.id, payload);
      toast.success('Contact updated successfully!');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact');
    } finally { 
      setLoading(false); 
    }
  };

  if (!contact) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-slide-in">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
              {contact.firstName?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Edit Contact</h2>
              <p className="text-xs text-gray-500">{contact.firstName} {contact.lastName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Personal Info */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Personal Info</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>First Name <span className="text-red-500">*</span></label>
                  <input type="text" name="firstName" value={form.firstName}
                    onChange={handleChange} placeholder="First name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Last Name</label>
                  <input type="text" name="lastName" value={form.lastName}
                    onChange={handleChange} placeholder="Last name" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" name="email" value={form.email}
                  onChange={handleChange} placeholder="email@example.com" 
                  className={`${inputCls} ${errors.email ? 'border-red-500 bg-red-50' : ''}`} />
                {errors.email && <p className="text-red-600 text-[10px] mt-1">⚠ {errors.email}</p>}
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input type="tel" name="phone" value={form.phone}
                  onChange={handleChange} placeholder="Phone number" 
                  className={`${inputCls} ${errors.phone ? 'border-red-500 bg-red-50' : ''}`} />
                {errors.phone && <p className="text-red-600 text-[10px] mt-1">⚠ {errors.phone}</p>}
              </div>
              <div>
                <label className={labelCls}>LinkedIn URL</label>
                <input type="text" name="linkedInUrl" value={form.linkedInUrl}
                  onChange={handleChange} placeholder="linkedin.com/in/..." className={inputCls} />
              </div>
            </div>
          </section>

          {/* Work Info */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Work Info</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Company Name</label>
                <Select
                  options={companies}
                  value={form.companyName}
                  onChange={(opt) => handleSelectChange('companyName', opt)}
                  isLoading={companiesLoading}
                  isClearable
                  placeholder="Select Company"
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className={labelCls}>Job Title</label>
                <input type="text" name="jobTitle" value={form.jobTitle}
                  onChange={handleChange} placeholder="Job title" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Department</label>
                <select name="department" value={form.department} onChange={handleChange} className={inputCls}>
                  <option value="">Select Department</option>
                  <option value="EXECUTIVE_LEADERSHIP">Executive Leadership</option>
                  <option value="HUMAN_RESOURCES">Human Resources</option>
                  <option value="TALENT_ACQUISITION">Talent Acquisition</option>
                  <option value="SALES">Sales</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="CUSTOMER_SUPPORT">Customer Support</option>
                  <option value="FINANCE">Finance</option>
                  <option value="LEGAL">Legal</option>
                  <option value="IT">IT</option>
                  <option value="ENGINEERING">Engineering</option>
                  <option value="PRODUCT_MANAGEMENT">Product Management</option>
                  <option value="DESIGN_UX">Design & UX</option>
                  <option value="OPERATIONS">Operations</option>
                  <option value="BUSINESS_DEVELOPMENT">Business Development</option>
                  <option value="PROCUREMENT">Procurement</option>
                  <option value="MANUFACTURING">Manufacturing</option>
                  <option value="LOGISTICS">Logistics</option>
                  <option value="QUALITY_ASSURANCE">Quality Assurance</option>
                  <option value="RESEARCH_DEVELOPMENT">Research & Development</option>
                  <option value="TRAINING_LD">Training & L&D</option>
                  <option value="SECURITY_RISK">Security & Risk</option>
                  <option value="PR_COMMUNICATIONS">PR & Communications</option>
                  <option value="FACILITIES_MANAGEMENT">Facilities Management</option>
                  <option value="CONSULTING">Consulting</option>
                  <option value="HEALTHCARE">Healthcare</option>
                  <option value="EDUCATION">Education</option>
                  <option value="DATA_ANALYTICS">Data & Analytics</option>
                  <option value="INVESTOR_RELATIONS">Investor Relations</option>
                  <option value="CORPORATE_STRATEGY">Corporate Strategy</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Assigned To</label>
                <Select options={employees} value={form.assignedTo} onChange={(opt) => handleSelectChange('assignedTo', opt)}
                  isClearable isLoading={employeesLoading} placeholder="Select employee"
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </section>

          {/* Communication Preferences */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Communication Preferences</p>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="doNotCall" checked={form.doNotCall || false} onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-xs font-medium text-gray-600">Do Not Call</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="doNotEmail" checked={form.doNotEmail || false} onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-xs font-medium text-gray-600">Do Not Email</span>
              </label>
            </div>
          </section>

          {/* Lead Info */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Lead Info</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Lead Status</label>
                <select name="leadStatus" value={form.leadStatus} onChange={handleChange} className={inputCls}>
                  <option value="">Select Status</option>
                  <option value="NEW">New</option>
                  <option value="PROSPECT">Prospect</option>
                  <option value="CONTACTED">Contacted</option>
                  <option value="QUALIFIED_LEAD">Qualified Lead</option>
                  <option value="UNQUALIFIED_LEAD">Unqualified Lead</option>
                  <option value="NURTURING">Nurturing</option>
                  <option value="MEETING_SCHEDULED">Meeting Scheduled</option>
                  <option value="PROPOSAL_SENT">Proposal Sent</option>
                  <option value="NEGOTIATION">Negotiation</option>
                  <option value="CONTRACT_SENT">Contract Sent</option>
                  <option value="WON_CUSTOMER">Won Customer</option>
                  <option value="LOST">Lost</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Lead Source</label>
                <select name="leadSource" value={form.leadSource} onChange={handleChange} className={inputCls}>
                  <option value="">Select Source</option>
                  <option value="GOOGLE_ADS">Google Ads</option>
                  <option value="LINKEDIN_ADS">LinkedIn Ads</option>
                  <option value="FACEBOOK_ADS">Facebook Ads</option>
                  <option value="INSTAGRAM_ADS">Instagram Ads</option>
                  <option value="WHATSAPP_ADS">WhatsApp Ads</option>
                  <option value="TELEGRAM_ADS">Telegram Ads</option>
                  <option value="CONTACT_FORM">Contact Form</option>
                  <option value="EMAIL_CAMPAIGN">Email Campaign</option>
                  <option value="COLD_CALLING">Cold Calling</option>
                  <option value="REFERRAL">Referral</option>
                  <option value="EVENTS">Events</option>
                  <option value="TRADE_SHOWS">Trade Shows</option>
                  <option value="PARTNERSHIP">Partnership</option>
                  <option value="ORGANIC_SEARCH">Organic Search</option>
                  <option value="PAPER_ADS">Paper Ads</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-gray-50 flex gap-3 justify-end">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-60">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

export default EditContactModal;